import { useEffect, useState } from "react";
import { AccordionRow } from "../components/summaryAccordion";
import { createSummaryData } from "../utils/summaryUtils";
import { Report } from "../api/models/report.models";
import { EcomodClass } from "../api/models/ecomodClass.models";
import { SupClass } from "../api/models/supClass.models";
import { Price } from "../api/models/prices.models";
import { Locale } from "../types/ui-types";

interface UseSummaryDataProps {
  reportData: Report | undefined;
  prevYearReport: Report | undefined | null;
  ecomodClasses: EcomodClass[] | undefined;
  supClasses: SupClass[] | undefined;
  pricesData: Price[] | undefined;
  locale: Locale;
}

interface SummaryData {
  consumerData: AccordionRow[] | undefined;
  prevYearConsumerData: AccordionRow[] | undefined;
  businessData: AccordionRow[] | undefined;
  prevYearBusinessData: AccordionRow[] | undefined;
  supData: AccordionRow[] | undefined;
  prevYearSupData: AccordionRow[] | undefined;
  totalCost: string;
  consumerBusinessCost: string;
  supCost: string;
}

export default function useSummaryData({
  reportData,
  prevYearReport,
  ecomodClasses,
  supClasses,
  pricesData,
  locale,
}: UseSummaryDataProps): SummaryData {
  const [consumerData, setConsumerData] = useState<AccordionRow[]>();
  const [prevYearConsumerData, setPrevYearConsumerData] =
    useState<AccordionRow[]>();
  const [businessData, setBusinessData] = useState<AccordionRow[]>();
  const [prevYearBusinessData, setPrevYearBusinessData] =
    useState<AccordionRow[]>();
  const [supData, setSupData] = useState<AccordionRow[]>();
  const [prevYearSupData, setPrevYearSupData] = useState<AccordionRow[]>();
  const [totalCost, setTotalCost] = useState<string>("");
  const [consumerBusinessCost, setConsumerBusinessCost] =
    useState<string>("");
  const [supCost, setSupCost] = useState<string>("");

  useEffect(() => {
    // Only process if we have the minimum required data
    if (!ecomodClasses || !supClasses || !pricesData) {
      return;
    }

    // Process current year data
    if (reportData) {
      const { consumer, business, sup } = createSummaryData(
        reportData,
        ecomodClasses,
        supClasses,
        pricesData,
        locale,
      );

      setConsumerData(consumer);
      setBusinessData(business);
      setSupData(sup);

      // Calculate total cost from all current year data
      const total = [...consumer, ...business, ...sup].reduce(
        (sum, row) => sum + (row.price || 0),
        0,
      );
      setTotalCost(total.toFixed(2));

      const consumerBusinessTotal = [...consumer, ...business].reduce(
        (sum, row) => sum + (row.price || 0),
        0,
      );
      setConsumerBusinessCost(consumerBusinessTotal.toFixed(2));

      const supTotal = sup.reduce((sum, row) => sum + (row.price || 0), 0);
      setSupCost(supTotal.toFixed(2));
    }

    // Process previous year data
    if (prevYearReport) {
      const { consumer, business, sup } = createSummaryData(
        prevYearReport,
        ecomodClasses,
        supClasses,
        pricesData,
        locale,
      );

      setPrevYearConsumerData(consumer);
      setPrevYearBusinessData(business);
      setPrevYearSupData(sup);
    } else {
      // Clear previous year data if no previous year report exists
      setPrevYearConsumerData(undefined);
      setPrevYearBusinessData(undefined);
      setPrevYearSupData(undefined);
    }
  }, [
    reportData,
    prevYearReport,
    ecomodClasses,
    supClasses,
    pricesData,
    locale,
  ]);

  return {
    consumerData,
    prevYearConsumerData,
    businessData,
    prevYearBusinessData,
    supData,
    prevYearSupData,
    totalCost,
    consumerBusinessCost,
    supCost,
  };
}
